import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Descriptions,
  Drawer,
  Empty,
  Layout,
  List,
  Popconfirm,
  Select,
  Space,
  Spin,
  Tabs,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import {
  CheckOutlined,
  ClearOutlined,
  CopyOutlined,
  FileSearchOutlined,
  InfoCircleOutlined,
  LinkOutlined,
  MessageOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { getApiErrorMessage, serverFetch } from '@api/server';
import { normalizeMarkdownTables } from '../markdown/markdown-view';
import {
  clearDialogContext,
  formatDate,
  getDialogContext,
  getDialogTitle,
} from './dialogs';

const { Content } = Layout;
const { Text, Paragraph } = Typography;

const MESSAGE_DETAILS_DRAWER_WIDTH_KEY = 'botva.messages.detailsDrawerWidth';
const DIALOG_CONTEXT_DRAWER_WIDTH_KEY = 'botva.dialogs.contextDrawerWidth';
const MIN_DRAWER_WIDTH = 480;
const DEFAULT_MESSAGE_DETAILS_DRAWER_WIDTH = 720;
const DEFAULT_DIALOG_CONTEXT_DRAWER_WIDTH = 640;
const DRAWER_VIEWPORT_GAP = 48;

const getInitialDrawerWidth = (storageKey, defaultWidth) => {
  const savedWidth = Number(localStorage.getItem(storageKey));
  if (Number.isFinite(savedWidth) && savedWidth >= MIN_DRAWER_WIDTH) {
    return savedWidth;
  }

  return defaultWidth;
};

const clampDrawerWidth = (width) => {
  const maxWidth = Math.max(MIN_DRAWER_WIDTH, window.innerWidth - DRAWER_VIEWPORT_GAP);
  return Math.min(Math.max(width, MIN_DRAWER_WIDTH), maxWidth);
};

const formatContextJson = (context) => {
  if (context == null) {
    return '';
  }

  if (typeof context === 'string') {
    try {
      return JSON.stringify(JSON.parse(context), null, 2);
    } catch {
      return context;
    }
  }

  try {
    return JSON.stringify(context, null, 2);
  } catch {
    return String(context);
  }
};

const hasDialogContextValue = (context) => {
  if (context == null || context === '') {
    return false;
  }

  if (Array.isArray(context)) {
    return context.length > 0;
  }

  if (typeof context === 'object') {
    return Object.keys(context).length > 0;
  }

  return true;
};

const extractText = (node) => {
  if (node == null || typeof node === 'boolean') {
    return '';
  }
  if (typeof node === 'string' || typeof node === 'number') {
    return String(node);
  }
  if (Array.isArray(node)) {
    return node.map(extractText).join('');
  }
  if (node.props?.children) {
    return extractText(node.props.children);
  }
  return '';
};

const CodeBlock = ({ children, ...props }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    try {
      await navigator.clipboard.writeText(extractText(children));
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      setCopied(false);
    }
  };

  return (
    <div className="message-code-block">
      <div className="message-code-copy">
        <Tooltip title={copied ? 'Скопировано' : 'Копировать'}>
          <Button
            type="text"
            size="small"
            icon={copied ? <CheckOutlined /> : <CopyOutlined />}
            onClick={handleCopy}
            aria-label="Копировать код"
          />
        </Tooltip>
      </div>
      <pre {...props}>{children}</pre>
    </div>
  );
};

const markdownComponents = {
  pre: CodeBlock,
};

export const getDialogMessages = (dialogId) => serverFetch(`message/list/${dialogId}`);
export const getMessage = (messageId) => serverFetch(`message/${messageId}`);
export const clearMessageContext = (messageId) => serverFetch(`message/clear_context/${messageId}`, {
  method: 'PUT',
});
export const updateMessageOperator = (messageId, operator) => serverFetch('message', {
  method: 'PUT',
  bodyData: { id: messageId, operator },
});

const MessageItem = ({
  item,
  agentOptions,
  isContextClearing,
  isOperatorUpdating,
  onClearMessageContext,
  onUpdateMessageOperator,
  onOpenDetails,
}) => {
  const isUser = item.direction === 1;
  const hasResponseId = item.response_id !== undefined && item.response_id !== null && item.response_id !== '';
  const canEditOperator = !isUser && agentOptions.length > 0;
  const operatorOptions = item.operator && !agentOptions.some((option) => option.value === item.operator)
    ? [...agentOptions, { value: item.operator, label: item.operator }]
    : agentOptions;

  return (
    <div className={`message-row ${isUser ? 'message-row-inbound' : 'message-row-outbound'}`}>
      <Card className="message-card" size="small">
        <Space direction="vertical" size={4}>
          <Space size={8} wrap>
            <Tag color={isUser ? 'blue' : 'green'}>
              {isUser ? 'Клиент' : 'Бот'}
            </Tag>
            {canEditOperator ? (
              <Select
                className="message-operator-select"
                size="small"
                value={item.operator || undefined}
                options={operatorOptions}
                placeholder="Агент"
                loading={isOperatorUpdating}
                disabled={isOperatorUpdating}
                onChange={(operator) => onUpdateMessageOperator(item, operator)}
              />
            ) : (
              item.operator && <Text type="secondary">{item.operator}</Text>
            )}
            <Text type="secondary">{formatDate(item.createdon)}</Text>
            {item.url && (
              <Tooltip title={item.url}>
                <LinkOutlined className="message-url-icon" />
              </Tooltip>
            )}
            {hasResponseId && (
              <Popconfirm
                title="Очистить контекст?"
                description="Поле response_id будет очищено."
                okText="Очистить"
                cancelText="Отмена"
                okButtonProps={{ danger: true, loading: isContextClearing }}
                onConfirm={() => onClearMessageContext(item)}
              >
                <Button type="link" size="small" className="message-response-id">
                  {item.response_id}
                </Button>
              </Popconfirm>
            )}
            <Button
              type="link"
              size="small"
              icon={<InfoCircleOutlined />}
              onClick={() => onOpenDetails(item)}
            >
              Подробно
            </Button>
          </Space>
          {item.body ? (
            <div className="message-markdown">
              <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
                {normalizeMarkdownTables(item.body)}
              </ReactMarkdown>
            </div>
          ) : (
            <Text>Пустое сообщение</Text>
          )}
        </Space>
      </Card>
    </div>
  );
};

const DialogMessages = ({
  selectedDialog,
  messages,
  messagesError,
  isMessagesLoading,
  agentOptions = [],
  clearingMessageId,
  updatingOperatorMessageId,
  onReloadMessages,
  onClearMessageContext,
  onUpdateMessageOperator,
  onDialogContextCleared,
}) => {
  const [isContextOpen, setIsContextOpen] = useState(false);
  const [isContextLoading, setIsContextLoading] = useState(false);
  const [isContextClearing, setIsContextClearing] = useState(false);
  const [contextError, setContextError] = useState('');
  const [dialogContext, setDialogContext] = useState(null);
  const [hasContext, setHasContext] = useState(false);
  const [detailsMessageId, setDetailsMessageId] = useState(null);
  const [detailsMessage, setDetailsMessage] = useState(null);
  const [isDetailsLoading, setIsDetailsLoading] = useState(false);
  const [detailsError, setDetailsError] = useState('');
  const [detailsDrawerWidth, setDetailsDrawerWidth] = useState(() => (
    getInitialDrawerWidth(MESSAGE_DETAILS_DRAWER_WIDTH_KEY, DEFAULT_MESSAGE_DETAILS_DRAWER_WIDTH)
  ));
  const [contextDrawerWidth, setContextDrawerWidth] = useState(() => (
    getInitialDrawerWidth(DIALOG_CONTEXT_DRAWER_WIDTH_KEY, DEFAULT_DIALOG_CONTEXT_DRAWER_WIDTH)
  ));

  useEffect(() => {
    setIsContextOpen(false);
    setDialogContext(null);
    setContextError('');
    setHasContext(hasDialogContextValue(selectedDialog?.context));
    setDetailsMessageId(null);
    setDetailsMessage(null);
    setDetailsError('');
  }, [selectedDialog?.id, selectedDialog?.context]);

  const handleOpenContext = useCallback(async () => {
    if (!selectedDialog?.id) {
      return;
    }

    setContextError('');
    setIsContextOpen(true);
    setIsContextLoading(true);
    try {
      const result = await getDialogContext(selectedDialog.id);
      const nextContext = result?.context ?? null;
      setDialogContext(nextContext);
      setHasContext(hasDialogContextValue(nextContext));
    } catch (error) {
      setDialogContext(null);
      setHasContext(false);
      setContextError(getApiErrorMessage(error));
    } finally {
      setIsContextLoading(false);
    }
  }, [selectedDialog?.id]);

  const handleClearDialogContext = useCallback(async () => {
    if (!selectedDialog?.id || !hasContext) {
      return;
    }

    setContextError('');
    setIsContextClearing(true);
    try {
      await clearDialogContext(selectedDialog.id);
      setDialogContext(null);
      setHasContext(false);
      onDialogContextCleared?.(selectedDialog.id);
    } catch (error) {
      setContextError(getApiErrorMessage(error));
    } finally {
      setIsContextClearing(false);
    }
  }, [hasContext, onDialogContextCleared, selectedDialog?.id]);

  const handleOpenDetails = useCallback(async (message) => {
    if (!message?.id) {
      return;
    }

    setDetailsError('');
    setDetailsMessageId(message.id);
    setDetailsMessage(null);
    setIsDetailsLoading(true);
    try {
      const result = await getMessage(message.id);
      setDetailsMessage(result || null);
    } catch (error) {
      setDetailsMessage(null);
      setDetailsError(getApiErrorMessage(error));
    } finally {
      setIsDetailsLoading(false);
    }
  }, []);

  const handleCloseDetails = useCallback(() => {
    setDetailsMessageId(null);
    setDetailsMessage(null);
    setDetailsError('');
  }, []);

  const createDrawerResizeHandler = useCallback((storageKey, setWidth) => (event) => {
    event.preventDefault();

    const handleMouseMove = (moveEvent) => {
      const nextWidth = clampDrawerWidth(window.innerWidth - moveEvent.clientX);
      setWidth(nextWidth);
      localStorage.setItem(storageKey, String(nextWidth));
    };

    const handleMouseUp = () => {
      document.body.classList.remove('drawer-resizing');
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };

    document.body.classList.add('drawer-resizing');
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
  }, []);

  const handleDetailsResizeStart = useCallback(
    (event) => createDrawerResizeHandler(MESSAGE_DETAILS_DRAWER_WIDTH_KEY, setDetailsDrawerWidth)(event),
    [createDrawerResizeHandler],
  );

  const handleContextResizeStart = useCallback(
    (event) => createDrawerResizeHandler(DIALOG_CONTEXT_DRAWER_WIDTH_KEY, setContextDrawerWidth)(event),
    [createDrawerResizeHandler],
  );

  const contextText = formatContextJson(dialogContext);
  const askText = formatContextJson(detailsMessage?.ask);
  const answerText = formatContextJson(detailsMessage?.answer);
  const directionLabel = detailsMessage?.direction === 1
    ? 'Клиент'
    : detailsMessage?.direction === -1
      ? 'Бот'
      : detailsMessage?.direction;

  return (
    <Content className="messages-content">
      {!selectedDialog ? (
        <Empty
          className="messages-empty"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="Выберите диалог"
        />
      ) : (
        <Card
          className="messages-panel"
          title={
            <Space>
              <MessageOutlined />
              <span>{getDialogTitle(selectedDialog)}</span>
            </Space>
          }
          extra={
            <Space wrap>
              <Button
                icon={<FileSearchOutlined />}
                onClick={handleOpenContext}
                loading={isContextLoading}
              >
                Контекст
              </Button>
              <Popconfirm
                title="Очистить контекст диалога?"
                description="Поле context будет очищено."
                okText="Очистить"
                cancelText="Отмена"
                disabled={!hasContext}
                okButtonProps={{ danger: true, loading: isContextClearing }}
                onConfirm={handleClearDialogContext}
              >
                <Button
                  danger
                  icon={<ClearOutlined />}
                  loading={isContextClearing}
                  disabled={!hasContext}
                >
                  Очистить контекст
                </Button>
              </Popconfirm>
              <Button
                icon={<ReloadOutlined />}
                onClick={() => onReloadMessages(selectedDialog.id)}
                loading={isMessagesLoading}
              >
                Обновить
              </Button>
            </Space>
          }
        >
          {(messagesError || contextError) && (
            <Alert
              className="panel-alert"
              type="error"
              message={messagesError || contextError}
              showIcon
            />
          )}
          <Spin spinning={isMessagesLoading}>
            {messages.length === 0 ? (
              <Empty description="Сообщений пока нет" />
            ) : (
              <List
                className="messages-list"
                dataSource={messages}
                renderItem={(item) => (
                  <List.Item>
                    <MessageItem
                      item={item}
                      agentOptions={agentOptions}
                      isContextClearing={clearingMessageId === item.id}
                      isOperatorUpdating={updatingOperatorMessageId === item.id}
                      onClearMessageContext={onClearMessageContext}
                      onUpdateMessageOperator={onUpdateMessageOperator}
                      onOpenDetails={handleOpenDetails}
                    />
                  </List.Item>
                )}
              />
            )}
          </Spin>
        </Card>
      )}

      <Drawer
        className="user-drawer user-form-drawer agent-resizable-drawer"
        title={detailsMessageId ? `Сообщение #${detailsMessageId}` : 'Сообщение'}
        placement="right"
        width={detailsDrawerWidth}
        open={Boolean(detailsMessageId)}
        onClose={handleCloseDetails}
        destroyOnHidden
        footer={(
          <div className="user-drawer-footer">
            <Button onClick={handleCloseDetails}>Закрыть</Button>
          </div>
        )}
      >
        <div
          className="agent-drawer-resize-handle"
          role="separator"
          aria-orientation="vertical"
          aria-label="Изменить ширину окна сообщения"
          onMouseDown={handleDetailsResizeStart}
        />

        {detailsError && (
          <Alert className="user-alert" type="error" message={detailsError} showIcon />
        )}
        <Spin spinning={isDetailsLoading}>
          {detailsMessage ? (
            <Tabs
              items={[
                {
                  key: 'general',
                  label: 'Общая',
                  children: (
                    <Space direction="vertical" size={12} style={{ width: '100%' }}>
                      <Descriptions
                        className="message-details-descriptions"
                        column={1}
                        size="small"
                        bordered
                      >
                        <Descriptions.Item label="ID">{detailsMessage.id}</Descriptions.Item>
                        <Descriptions.Item label="Диалог">{detailsMessage.dialogid}</Descriptions.Item>
                        <Descriptions.Item label="Направление">{directionLabel}</Descriptions.Item>
                        <Descriptions.Item label="Оператор">
                          {detailsMessage.operator || '—'}
                        </Descriptions.Item>
                        <Descriptions.Item label="response_id">
                          {detailsMessage.response_id || '—'}
                        </Descriptions.Item>
                        <Descriptions.Item label="Создано">
                          {formatDate(detailsMessage.createdon)}
                        </Descriptions.Item>
                      </Descriptions>
                      <div>
                        <Text strong>Текст</Text>
                        {detailsMessage.body ? (
                          <div className="message-markdown message-details-body">
                            <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
                              {normalizeMarkdownTables(detailsMessage.body)}
                            </ReactMarkdown>
                          </div>
                        ) : (
                          <Paragraph type="secondary">Пустое сообщение</Paragraph>
                        )}
                      </div>
                    </Space>
                  ),
                },
                {
                  key: 'ask',
                  label: 'Запрос',
                  children: askText ? (
                    <pre className="dialog-context-json">{askText}</pre>
                  ) : (
                    <Empty description="Запрос пуст" />
                  ),
                },
                {
                  key: 'answer',
                  label: 'Ответ',
                  children: answerText ? (
                    <pre className="dialog-context-json">{answerText}</pre>
                  ) : (
                    <Empty description="Ответ пуст" />
                  ),
                },
              ]}
            />
          ) : !isDetailsLoading && !detailsError ? (
            <Empty description="Нет данных" />
          ) : null}
        </Spin>
      </Drawer>

      <Drawer
        className="user-drawer user-form-drawer agent-resizable-drawer"
        title={`Контекст диалога #${selectedDialog?.id || ''}`}
        placement="right"
        width={contextDrawerWidth}
        open={isContextOpen}
        onClose={() => setIsContextOpen(false)}
        destroyOnHidden
        footer={(
          <div className="user-drawer-footer">
            <Popconfirm
              title="Очистить контекст диалога?"
              description="Поле context будет очищено."
              okText="Очистить"
              cancelText="Отмена"
              disabled={!hasContext}
              okButtonProps={{ danger: true, loading: isContextClearing }}
              onConfirm={handleClearDialogContext}
            >
              <Button
                danger
                icon={<ClearOutlined />}
                loading={isContextClearing}
                disabled={!hasContext}
              >
                Очистить
              </Button>
            </Popconfirm>
            <Button onClick={() => setIsContextOpen(false)}>Закрыть</Button>
          </div>
        )}
      >
        <div
          className="agent-drawer-resize-handle"
          role="separator"
          aria-orientation="vertical"
          aria-label="Изменить ширину окна контекста"
          onMouseDown={handleContextResizeStart}
        />

        {contextError && (
          <Alert className="user-alert" type="error" message={contextError} showIcon />
        )}
        <Spin spinning={isContextLoading}>
          {contextText ? (
            <pre className="dialog-context-json">{contextText}</pre>
          ) : (
            <Empty description="Контекст пуст" />
          )}
        </Spin>
      </Drawer>
    </Content>
  );
};

export default DialogMessages;
